import { useState, useEffect } from "react";
import "./reports.scss";
import { TextField } from "@mui/material";
import * as XLSX from "xlsx";
import { sanitizeInput } from "../../utils/inputValidations";
import ViewFilesModal from "./components/ViewFilesModal";
import ScenarioModal from "./components/ScenarioModal";
import { getSelectionViewSummary } from "../../Api";
import Swal from "sweetalert2";

function Report() {
  const [step6Files, setStep6Files] = useState([]);
  const [showCards, setShowCards] = useState(false);
  // const [selectedReport, setSelectedReport] = useState("efficiency");
  // const [currentPage, setCurrentPage] = useState(1);
  const [showScenarioModal, setShowScenarioModal] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [hitPage, setHitPage] = useState(1);
  const [unhitPage, setUnhitPage] = useState(1);
  const [scenarioPage, setScenarioPage] = useState(1);

  const [searchTerm, setSearchTerm] = useState("");
  const [viewType, setViewType] = useState(null);

  const [modalCurrentPage, setModalCurrentPage] = useState(1);

  // API data states
  const [apiData, setApiData] = useState(null);
  const [outputFilename, setOutputFilename] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const itemsPerPage = 5;
  const modalItemsPerPage = 5;

  // Fetch data from API
  const fetchApiData = async () => {
    setIsLoading(true);
    try {
      const bank_code = localStorage.getItem("bank_code");
      const correl_id = localStorage.getItem("correl_id");

      if (!bank_code || !correl_id) {
        console.error("Missing bank_code or correl_id in localStorage");
        Swal.fire({
          icon: "error",
          title: "Configuration Error",
          text: "Missing bank code or correlation ID. Please check your configuration.",
        });
        return;
      }

      Swal.fire({
        html: `<img src=${require("../../assets/loader.gif")} style="width: 50px" alt="loader">`,
        showConfirmButton: false,
        heightAuto: false,
        customClass: { popup: "loader-swal" },
        allowOutsideClick: false,
      });
      const response = await getSelectionViewSummary({
        bank_code,
        correl_id,
        page: 1,
        pageSize: 10,
      });
      Swal.close();

      if (response && response.status === "S") {
        setApiData(response);
        // Set output filename from API response
        if (response.result_info && response.result_info.length > 0) {
          setOutputFilename(response.result_info[0]?.output_filename || "");
        }
        console.log("API data fetched successfully:", response);

      } else {
        console.error("API response error:", response);
        Swal.fire({
          icon: "warning",
          title: "API Error",
          text: response?.message || "Failed to fetch data from server.",
        });
      }
    } catch (error) {
      console.error("Error fetching API data:", error);
      Swal.fire({
        icon: "error",
        title: "Network Error",
        text: "Failed to connect to server. Please check your internet connection.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data when component mounts
  useEffect(() => {
    fetchApiData();
  }, []);

  // Transform API data to match the expected format for ScenarioModal
  const transformApiDataToScenarioSummary = () => {
    if (!apiData || !apiData.result_info) return [];

    return apiData.result_info.map((item, index) => ({
      id: index + 1,
      scenarioName: item.scenario || `Scenario ${index + 1}`,
      totalRecords: item.count || 0,
      hitCount: Math.floor((item.count || 0) * 0.15), // 15% hit rate as example
      hitPercentage: 15,
      unhitCount: Math.floor((item.count || 0) * 0.85), // 85% unhit rate as example
      unhitPercentage: 85,
      details: {
        hits: [
          {
            name: "John Smith",
            processedName: "JOHNSMITH",
            violations: [
              "Name without spaces: JOHNSMITH",
              "Single word format: JOHNSMITH",
              "No word separation: JOHNSMITH",
            ],
          },
          {
            name: "Sarah Johnson",
            processedName: "SARAHJOHNSON",
            violations: [
              "Name without spaces: SARAHJOHNSON",
              "Single word format: SARAHJOHNSON",
              "No word separation: SARAHJOHNSON",
            ],
          },
          {
            name: "Michael Brown",
            processedName: "MICHAELBROWN",
            violations: [
              "Name without spaces: MICHAELBROWN",
              "Single word format: MICHAELBROWN",
              "No word separation: MICHAELBROWN",
            ],
          },
        ],
        unhits: [
          {
            name: "David Wilson",
            processedName: "DAVID WILSON",
            variations: [
              "Original format: DAVID WILSON",
              "Two word format: DAVID WILSON",
              "Proper spacing: DAVID WILSON",
            ],
          },
          {
            name: "Emma Davis",
            processedName: "EMMA DAVIS",
            variations: [
              "Original format: EMMA DAVIS",
              "Two word format: EMMA DAVIS",
              "Proper spacing: EMMA DAVIS",
            ],
          },
          {
            name: "James Miller",
            processedName: "JAMES MILLER",
            variations: [
              "Original format: JAMES MILLER",
              "Two word format: JAMES MILLER",
              "Proper spacing: JAMES MILLER",
            ],
          },
        ],
      },
    }));
  };

  // Use dynamic data from API or fallback to static data
  const scenarioSummaryData =
    transformApiDataToScenarioSummary().length > 0
      ? transformApiDataToScenarioSummary()
      : [
          {
            id: 1,
            scenarioName: "Run Together",
            totalRecords: 1000,
            hitCount: 150,
            hitPercentage: 15,
            unhitCount: 850,
            unhitPercentage: 85,
            details: {
              hits: [
                {
                  name: "John Smith",
                  processedName: "JOHNSMITH",
                  violations: [
                    "Name without spaces: JOHNSMITH",
                    "Single word format: JOHNSMITH",
                    "No word separation: JOHNSMITH",
                  ],
                },
                {
                  name: "Sarah Johnson",
                  processedName: "SARAHJOHNSON",
                  violations: [
                    "Name without spaces: SARAHJOHNSON",
                    "Single word format: SARAHJOHNSON",
                    "No word separation: SARAHJOHNSON",
                  ],
                },
                {
                  name: "Michael Brown",
                  processedName: "MICHAELBROWN",
                  violations: [
                    "Name without spaces: MICHAELBROWN",
                    "Single word format: MICHAELBROWN",
                    "No word separation: MICHAELBROWN",
                  ],
                },
              ],
              unhits: [
                {
                  name: "David Wilson",
                  processedName: "DAVID WILSON",
                  variations: [
                    "Original format: DAVID WILSON",
                    "Two word format: DAVID WILSON",
                    "Proper spacing: DAVID WILSON",
                  ],
                },
                {
                  name: "Emma Davis",
                  processedName: "EMMA DAVIS",
                  variations: [
                    "Original format: EMMA DAVIS",
                    "Two word format: EMMA DAVIS",
                    "Proper spacing: EMMA DAVIS",
                  ],
                },
                {
                  name: "James Miller",
                  processedName: "JAMES MILLER",
                  variations: [
                    "Original format: JAMES MILLER",
                    "Two word format: JAMES MILLER",
                    "Proper spacing: JAMES MILLER",
                  ],
                },
              ],
            },
          },
          {
            id: 2,
            scenarioName: "Abbreviations",
            totalRecords: 800,
            hitCount: 80,
            hitPercentage: 10,
            unhitCount: 720,
            unhitPercentage: 90,
            details: {
              hits: [
                {
                  name: "Robert Taylor",
                  processedName: "ROB TAYLOR",
                  violations: [
                    "Abbreviated first name: ROB TAYLOR",
                    "Shortened format: ROB TAYLOR",
                    "Initial format: R TAYLOR",
                  ],
                },
                {
                  name: "Lisa Anderson",
                  processedName: "L ANDERSON",
                  violations: [
                    "Initial only: L ANDERSON",
                    "Single letter format: L ANDERSON",
                    "Abbreviated format: L ANDERSON",
                  ],
                },
                {
                  name: "Thomas White",
                  processedName: "TOM WHITE",
                  violations: [
                    "Shortened first name: TOM WHITE",
                    "Nickname format: TOM WHITE",
                    "Abbreviated format: T WHITE",
                  ],
                },
              ],
              unhits: [
                {
                  name: "Jennifer Lee",
                  processedName: "JENNIFER LEE",
                  variations: [
                    "Full name format: JENNIFER LEE",
                    "Complete format: JENNIFER LEE",
                    "Proper format: JENNIFER LEE",
                  ],
                },
                {
                  name: "William Clark",
                  processedName: "WILLIAM CLARK",
                  variations: [
                    "Full name format: WILLIAM CLARK",
                    "Complete format: WILLIAM CLARK",
                    "Proper format: WILLIAM CLARK",
                  ],
                },
                {
                  name: "Mary Hall",
                  processedName: "MARY HALL",
                  variations: [
                    "Full name format: MARY HALL",
                    "Complete format: MARY HALL",
                    "Proper format: MARY HALL",
                  ],
                },
              ],
            },
          },
          {
            id: 3,
            scenarioName: "Aliases",
            totalRecords: 1200,
            hitCount: 240,
            hitPercentage: 20,
            unhitCount: 960,
            unhitPercentage: 80,
            details: {
              hits: [
                {
                  name: "Daniel Moore",
                  processedName: "DANNY MOORE",
                  violations: [
                    "Nickname used: DANNY MOORE",
                    "Alternative name: DANNY MOORE",
                    "Common alias: DANNY MOORE",
                  ],
                },
                {
                  name: "Patricia Lewis",
                  processedName: "PAT LEWIS",
                  violations: [
                    "Shortened name: PAT LEWIS",
                    "Nickname format: PAT LEWIS",
                    "Alternative format: PAT LEWIS",
                  ],
                },
                {
                  name: "Christopher Walker",
                  processedName: "CHRIS WALKER",
                  violations: [
                    "Common nickname: CHRIS WALKER",
                    "Shortened format: CHRIS WALKER",
                    "Alternative name: CHRIS WALKER",
                  ],
                },
              ],
              unhits: [
                {
                  name: "Elizabeth Young",
                  processedName: "ELIZABETH YOUNG",
                  variations: [
                    "Full name format: ELIZABETH YOUNG",
                    "Complete format: ELIZABETH YOUNG",
                    "Proper format: ELIZABETH YOUNG",
                  ],
                },
                {
                  name: "Matthew Allen",
                  processedName: "MATTHEW ALLEN",
                  variations: [
                    "Full name format: MATTHEW ALLEN",
                    "Complete format: MATTHEW ALLEN",
                    "Proper format: MATTHEW ALLEN",
                  ],
                },
                {
                  name: "Nancy King",
                  processedName: "NANCY KING",
                  variations: [
                    "Full name format: NANCY KING",
                    "Complete format: NANCY KING",
                    "Proper format: NANCY KING",
                  ],
                },
              ],
            },
          },
        ];

  const [selectedHit, setSelectedHit] = useState(null);
  const [selectedUnhit, setSelectedUnhit] = useState(null);

  const handleHitClick = (hit) => {
    setSelectedHit(hit);
  };

  // Add pagination functions
  // const handleFirstPage = () => {
  //   setCurrentPage(1);
  // };

  // const handleLastPage = () => {
  //   setCurrentPage(Math.ceil(10 / itemsPerPage)); // 10 is the total number of rows in the table
  // };

  // const handlePrevPage = () => {
  //   setCurrentPage((prev) => Math.max(prev - 1, 1));
  // };

  // const handleNextPage = () => {
  //   setCurrentPage((prev) => Math.min(prev + 1, Math.ceil(10 / itemsPerPage)));
  // };

  // const handleReportSelect = (report) => {
  //   setSelectedReport(report);
  // };

  const handleStep6FileChange = (event) => {
    const selectedFile = event.target.files[0];

    if (selectedFile) {
      // const allowedFileTypes = [
      //     "application/pdf",
      //     "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // XLSX
      //     "application/vnd.ms-excel", // XLS
      //     "text/csv", // CSV
      //     "application/csv" // Some browsers may use this MIME type for CSV
      // ];

      // if (allowedFileTypes.includes(fileType)) {
      setStep6Files([...step6Files, selectedFile]);
      // } else {
      //     Swal.fire({
      //         title: "Invalid File Format",
      //         text: "Only PDF, XLSX and CSV file formats are allowed!",
      //         icon: "warning",
      //         confirmButtonText: "Ok",
      //     });
      // }
    }
  };
  const handleDownload = (title) => {
    // Import the PDF file from assets
    import("../../assets/script.pdf").then((reportFile) => {
      const link = document.createElement("a");
      link.href = reportFile.default;
      link.download = `${title}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  // const handleExportXLSX = () => {
  //   // Get the table element
  //   const table = document.getElementById("fileTable");
  //   if (!table) {
  //     console.warn("Table not found");
  //     return;
  //   }

  //   // Extract headers
  //   const headers = [];
  //   const headerRow = table.querySelector("thead tr");
  //   if (headerRow) {
  //     headerRow.querySelectorAll("th").forEach((th) => {
  //       headers.push(th.textContent);
  //     });
  //   }

  //   // Extract data rows
  //   const rows = [];
  //   table.querySelectorAll("tbody tr").forEach((tr) => {
  //     const rowData = [];
  //     tr.querySelectorAll("td").forEach((td) => {
  //       rowData.push(td.textContent);
  //     });
  //     rows.push(rowData);
  //   });

  //   // Create the sheet data with headers followed by rows
  //   const sheetData = [headers, ...rows];

  //   // Create worksheet and workbook
  //   const ws = XLSX.utils.aoa_to_sheet(sheetData);
  //   const wb = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(wb, ws, "Table Data");

  //   // Generate file name with current date

  //   const fileName = `report_master.xlsx`;

  //   // Export file
  //   XLSX.writeFile(wb, fileName);
  // };

  const handleCloseScenarioModal = () => {
    setShowScenarioModal(false);
    setSelectedScenario(null);
  };

  // Add pagination functions for hits
  // const handleHitFirstPage = () => {
  //   setHitPage(1);
  // };

  // const handleHitLastPage = () => {
  //   setHitPage(Math.ceil(selectedScenario?.details.hits.length / itemsPerPage));
  // };

  // const handleHitPrevPage = () => {
  //   setHitPage((prev) => Math.max(prev - 1, 1));
  // };

  // const handleHitNextPage = () => {
  //   setHitPage((prev) =>
  //     Math.min(
  //       prev + 1,
  //       Math.ceil(selectedScenario?.details.hits.length / itemsPerPage)
  //     )
  //   );
  // };

  // Add pagination functions for unhits
  // const handleUnhitFirstPage = () => {
  //   setUnhitPage(1);
  // };

  // const handleUnhitLastPage = () => {
  //   setUnhitPage(
  //     Math.ceil(selectedScenario?.details.unhits.length / itemsPerPage)
  //   );
  // };

  // const handleUnhitPrevPage = () => {
  //   setUnhitPage((prev) => Math.max(prev - 1, 1));
  // };

  // const handleUnhitNextPage = () => {
  //   setUnhitPage((prev) =>
  //     Math.min(
  //       prev + 1,
  //       Math.ceil(selectedScenario?.details.unhits.length / itemsPerPage)
  //     )
  //   );
  // };

  // Add pagination functions for scenario summary
  // const handleScenarioFirstPage = () => {
  //   setScenarioPage(1);
  // };

  // const handleScenarioLastPage = () => {
  //   setScenarioPage(Math.ceil(scenarioSummaryData.length / itemsPerPage));
  // };

  // const handleScenarioPrevPage = () => {
  //   setScenarioPage((prev) => Math.max(prev - 1, 1));
  // };

  // const handleScenarioNextPage = () => {
  //   setScenarioPage((prev) =>
  //     Math.min(prev + 1, Math.ceil(scenarioSummaryData.length / itemsPerPage))
  //   );
  // };

  const handleSearch = (event) => {
    const rawValue = event.target.value;
    const cleanedValue = sanitizeInput(rawValue).toLowerCase();
    setSearchTerm(cleanedValue);
  };

  // For View Files Modal
  const [viewFilesModal, setViewFilesModal] = useState(false);

  // const handleModalFirstPage = () => setModalCurrentPage(1);
  // const handleModalLastPage = () =>
  //   setModalCurrentPage(Math.ceil(staticFileList.length / modalItemsPerPage));
  // const handleModalPrevPage = () =>
  //   setModalCurrentPage((prev) => Math.max(prev - 1, 1));
  // const handleModalNextPage = () =>
  //   setModalCurrentPage((prev) =>
  //     Math.min(prev + 1, Math.ceil(staticFileList.length / modalItemsPerPage))
  //   );

  const staticFileList = [
    {
      id: 1,
      fileName: "example-file-1.pdf",
      loadDate: "21-05-2025",
      loadTime: "10:00:00",
    },
    {
      id: 2,
      fileName: "example-file-2.pdf",
      loadDate: "20-05-2025",
      loadTime: "11:30:00",
    },
    {
      id: 3,
      fileName: "example-file-3.pdf",
      loadDate: "19-05-2025",
      loadTime: "09:15:00",
    },
  ];

  const handleScenarioClick = (scenario, type = null) => {
    setSelectedScenario(scenario);
    setSelectedHit(null);
    setSelectedUnhit(null);
    setViewType(type);
  };

  const filteredHits = selectedScenario?.details.hits.filter((hit) =>
    hit.name.toLowerCase().includes(searchTerm)
  );

  const handleViewFile = (file) => {
    setShowScenarioModal(true);
    setSelectedScenario(null); // Or set to a specific scenario if needed
    setSelectedHit(null);
    setSelectedUnhit(null);
    setViewType(null);
    setViewFilesModal(false); // Close the View Files modal when opening Efficiency modal
    // Set a fallback so the modal table always has data
    setTimeout(() => {
      setSelectedScenario(null);
      setViewType(null);
    }, 0);
  };

  // Fix for Back to Summary: ensure ScenarioModal gets scenarioSummaryData as data when selectedScenario is null and viewType is null
  return (
    <div className="efficiency-page">
      <div className="page-container">
        <div className="row justify-content-center">
          <div className="col-12">
            <h5 className="mb-4">Efficiency Report</h5>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <div>
              <button
                className="anchor-button"
                onClick={() => setViewFilesModal(true)}
                role="button"
              >
                View Files
              </button>
            </div>
            <div className="row align-items-end">
              <div className="col-12 col-md-6">
                <div className="min-width">
                  <TextField
                    fullWidth
                    disabled
                    label="Output File"
                    value={
                      isLoading
                        ? "Loading..."
                        : outputFilename ||
                          `outputfile_${
                            new Date().toISOString().split("T")[0]
                          }_${new Date()
                            .toLocaleTimeString()
                            .replace(/:/g, "-")}.pdf`
                    }
                    variant="outlined"
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <h6 className="">Alert File</h6>
                <div className="choose-file-input d-flex align-items-center mt-2">
                  <label
                    className="btn choose-file-btn me-2"
                    htmlFor="step6FileInput"
                  >
                    Choose File
                  </label>
                  <input
                    id="step6FileInput"
                    type="file"
                    // multiple
                    style={{ display: "none" }}
                    onChange={handleStep6FileChange}
                  />
                  <p
                    className="mb-0 choose-file-name flex-grow-1"
                    onClick={() =>
                      document.getElementById("step6FileInput").click()
                    }
                  >
                    {step6Files.length > 0
                      ? step6Files[step6Files.length - 1].name
                      : "No file chosen"}
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center mt-3">
              {!showCards && (
                <div className="text-center mt-3">
                  <button
                    className="btn createButton"
                    onClick={() => {
                      setShowScenarioModal(true);
                    }}
                  >
                    Submit
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <ScenarioModal
          isOpen={showScenarioModal}
          onClose={handleCloseScenarioModal}
          scenario={selectedScenario}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          viewType={viewType}
          data={
            viewType === "hits"
              ? filteredHits
              : viewType === "unhits"
              ? selectedScenario?.details.unhits
              : scenarioSummaryData
          }
          currentPage={
            viewType === "hits"
              ? hitPage
              : viewType === "unhits"
              ? unhitPage
              : scenarioPage
          }
          itemsPerPage={itemsPerPage}
          onPageChange={(page) => {
            if (viewType === "hits") setHitPage(page);
            else if (viewType === "unhits") setUnhitPage(page);
            else setScenarioPage(page);
          }}
          onScenarioClick={handleScenarioClick}
          selectedHit={selectedHit}
          selectedUnhit={selectedUnhit}
          onHitClick={setSelectedHit}
          onBackFromHit={() => {
            setSelectedHit(null);
            setViewType(null);
          }}
          onBackToSummary={() => {
            setSelectedHit(null);
            setSelectedScenario(null);
            setViewType(null);
            setScenarioPage(1);
            setHitPage(1);
            setUnhitPage(1);
            setSearchTerm("");
          }}
          onBackFromUnhit={() => 
          {
            setSelectedUnhit(null)
            setViewType(null)
            setScenarioPage(1);
            setHitPage(1);
            setUnhitPage(1);
            setSearchTerm("");
          }}
          onDownload={handleDownload}
        />
        <ViewFilesModal
          isOpen={viewFilesModal}
          onClose={() => setViewFilesModal(false)}
          staticFileList={staticFileList}
          currentPage={modalCurrentPage}
          itemsPerPage={modalItemsPerPage}
          onViewFile={handleViewFile}
          onPageChange={setModalCurrentPage}
        />
      </div>
    </div>
  );
}
export default Report;
